import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/App";
import { 
  BookOpen, 
  Lightbulb, 
  Target, 
  CheckCircle2, 
  ChevronRight,
  Quote,
  Brain,
  Sparkles,
  Award,
  Calendar,
  MessageSquare,
  Loader2,
  ArrowRight,
  BookMarked,
  Heart,
  Users,
  Briefcase,
  Home,
  Compass,
  Sun,
  Coffee
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  "Work Leadership": { icon: Briefcase, color: "from-blue-500 to-cyan-500", bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-400" },
  "Systems & Growth": { icon: Compass, color: "from-emerald-500 to-teal-500", bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400" },
  "Personal Motivation": { icon: Sun, color: "from-amber-500 to-orange-500", bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400" },
  "Fatherhood": { icon: Home, color: "from-violet-500 to-purple-500", bg: "bg-violet-500/10", border: "border-violet-500/30", text: "text-violet-400" },
  "Relationships": { icon: Heart, color: "from-pink-500 to-rose-500", bg: "bg-pink-500/10", border: "border-pink-500/30", text: "text-pink-400" },
  "Life Balance": { icon: Coffee, color: "from-indigo-500 to-blue-500", bg: "bg-indigo-500/10", border: "border-indigo-500/30", text: "text-indigo-400" }
};

export default function LeadershipAcademy() {
  const { user } = useAuth();
  const [dailyLesson, setDailyLesson] = useState(null);
  const [allThemes, setAllThemes] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [showReflectionDialog, setShowReflectionDialog] = useState(false);
  const [reflection, setReflection] = useState("");
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [showLessonDetail, setShowLessonDetail] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Get first name from user
  const firstName = user?.name?.split(' ')[0] || 'there';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [lessonRes, themesRes, progressRes] = await Promise.all([
        axios.get(`${API}/leadership/daily`, { withCredentials: true }),
        axios.get(`${API}/leadership/themes`, { withCredentials: true }),
        axios.get(`${API}/leadership/progress`, { withCredentials: true })
      ]);
      setDailyLesson(lessonRes.data);
      setAllThemes(themesRes.data.themes || []);
      setProgress(progressRes.data);
    } catch (error) {
      console.error("Error fetching leadership data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!dailyLesson?.lesson?.id) return;
    
    setCompleting(true);
    try {
      await axios.post(`${API}/leadership/complete`, null, {
        params: {
          lesson_id: dailyLesson.lesson.id,
          reflection: reflection || null
        },
        withCredentials: true
      });
      
      setShowReflectionDialog(false);
      setReflection("");
      const progressRes = await axios.get(`${API}/leadership/progress`, { withCredentials: true });
      setProgress(progressRes.data);
    } catch (error) {
      console.error("Error completing lesson:", error);
    } finally {
      setCompleting(false);
    }
  };

  const viewLessonDetail = async (lessonId) => {
    try {
      const res = await axios.get(`${API}/leadership/lesson/${lessonId}`, { withCredentials: true });
      setSelectedLesson(res.data);
      setShowLessonDetail(true);
    } catch (error) {
      console.error("Error fetching lesson:", error);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const filteredThemes = selectedCategory === "all" 
    ? allThemes 
    : allThemes.filter(t => t.category === selectedCategory);

  const categories = [...new Set(allThemes.map(t => t.category))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const lesson = dailyLesson?.lesson;
  const categoryConfig = lesson ? CATEGORY_CONFIG[lesson.category] : null;
  const CategoryIcon = categoryConfig?.icon || BookOpen;

  return (
    <div className="space-y-6">
      {/* Warm Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Sun className="w-5 h-5 text-white" />
            </div>
            {getGreeting()}, {firstName}
          </h1>
          <p className="text-muted-foreground mt-1">Your daily dose of wisdom for work, family, and life</p>
        </div>
        {progress && (
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Growth Streak</p>
              <p className="text-2xl font-bold text-amber-400 flex items-center gap-1">
                <Award className="w-5 h-5" />
                {progress.streak} days
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Empowering Quote Banner */}
      <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20">
        <CardContent className="py-4">
          <p className="text-center text-amber-100 italic">
            "You're not just a healthcare leader. You're a father, a partner, a whole person. Every role makes you stronger in the others."
          </p>
        </CardContent>
      </Card>

      {/* Progress */}
      {progress && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Your Growth Journey</span>
              <span className="text-sm font-medium text-amber-400">
                {progress.completed}/{progress.total_lessons} lessons explored
              </span>
            </div>
            <Progress value={progress.completion_rate} className="h-2" />
          </CardContent>
        </Card>
      )}

      {/* Today's Lesson */}
      {lesson && (
        <Card className={`bg-gradient-to-br from-zinc-900 to-zinc-800 ${categoryConfig?.border || 'border-amber-500/30'} overflow-hidden relative`}>
          <div className={`absolute top-0 right-0 w-64 h-64 bg-gradient-to-br ${categoryConfig?.color || 'from-amber-500 to-orange-500'} opacity-5 rounded-full blur-3xl`} />
          <CardHeader className="relative">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`${categoryConfig?.bg} ${categoryConfig?.text} ${categoryConfig?.border}`}>
                    <CategoryIcon className="w-3 h-3 mr-1" />
                    {lesson.category}
                  </Badge>
                  <Badge variant="outline" className="border-zinc-600 text-zinc-400">
                    Day {dailyLesson.day_number} of {dailyLesson.total_lessons}
                  </Badge>
                </div>
                <CardTitle className="text-xl text-white">{lesson.title}</CardTitle>
                <CardDescription className="text-base mt-1">
                  <span className={categoryConfig?.text}>{lesson.author}</span>
                  <span className="text-muted-foreground"> • </span>
                  <span className="text-muted-foreground italic">{lesson.book}</span>
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative space-y-6">
            {/* Key Principles */}
            <div>
              <h3 className={`text-sm font-semibold ${categoryConfig?.text} mb-3 flex items-center gap-2`}>
                <Lightbulb className="w-4 h-4" />
                Key Insights
              </h3>
              <ul className="space-y-2">
                {lesson.key_principles?.map((principle, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm text-zinc-300">
                    <ChevronRight className={`w-4 h-4 ${categoryConfig?.text} flex-shrink-0 mt-0.5`} />
                    {principle}
                  </li>
                ))}
              </ul>
            </div>

            {/* Application */}
            <div className={`p-4 rounded-xl ${categoryConfig?.bg} ${categoryConfig?.border} border`}>
              <h3 className={`text-sm font-semibold ${categoryConfig?.text} mb-2 flex items-center gap-2`}>
                <Target className="w-4 h-4" />
                How This Applies to You
              </h3>
              <p className="text-sm text-zinc-300">{lesson.application}</p>
            </div>

            {/* Reflection */}
            <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/20">
              <h3 className="text-sm font-semibold text-violet-400 mb-2 flex items-center gap-2">
                <Brain className="w-4 h-4" />
                Reflection
              </h3>
              <p className="text-sm text-zinc-300 italic">"{lesson.reflection_question}"</p>
            </div>

            {/* Action */}
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <h3 className="text-sm font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                <ArrowRight className="w-4 h-4" />
                Today's Action
              </h3>
              <p className="text-sm text-zinc-300 font-medium">{lesson.action_item}</p>
            </div>

            {/* Complete Button */}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                onClick={() => setShowReflectionDialog(true)}
                className={`bg-gradient-to-r ${categoryConfig?.color || 'from-amber-500 to-orange-500'}`}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                I've Grown Today
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={selectedCategory === "all" ? "default" : "outline"}
          onClick={() => setSelectedCategory("all")}
          className={selectedCategory === "all" ? "bg-gradient-to-r from-amber-500 to-orange-500" : "border-zinc-700"}
        >
          All Topics
        </Button>
        {categories.map(cat => {
          const config = CATEGORY_CONFIG[cat];
          const Icon = config?.icon || BookOpen;
          return (
            <Button
              key={cat}
              size="sm"
              variant={selectedCategory === cat ? "default" : "outline"}
              onClick={() => setSelectedCategory(cat)}
              className={selectedCategory === cat ? `bg-gradient-to-r ${config?.color}` : "border-zinc-700"}
            >
              <Icon className="w-3 h-3 mr-1" />
              {cat}
            </Button>
          );
        })}
      </div>

      {/* Lessons Library */}
      <Tabs defaultValue="library" className="w-full">
        <TabsList className="bg-zinc-900/50 border border-zinc-800">
          <TabsTrigger value="library">
            <BookMarked className="w-4 h-4 mr-2" />
            Wisdom Library ({filteredThemes.length})
          </TabsTrigger>
          <TabsTrigger value="reflections">
            <MessageSquare className="w-4 h-4 mr-2" />
            My Reflections
          </TabsTrigger>
        </TabsList>

        <TabsContent value="library" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredThemes.map((theme) => {
              const config = CATEGORY_CONFIG[theme.category];
              const Icon = config?.icon || BookOpen;
              return (
                <Card 
                  key={theme.id}
                  className={`bg-zinc-900/50 border-zinc-800 hover:${config?.border} transition-colors cursor-pointer`}
                  onClick={() => viewLessonDetail(theme.id)}
                >
                  <CardHeader className="pb-2">
                    <Badge className={`w-fit mb-2 ${config?.bg} ${config?.text} ${config?.border} text-xs`}>
                      <Icon className="w-3 h-3 mr-1" />
                      {theme.category}
                    </Badge>
                    <CardTitle className="text-base">{theme.title}</CardTitle>
                    <CardDescription className="text-sm">
                      {theme.author} • <span className="italic">{theme.book}</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="ghost" size="sm" className={`w-full ${config?.text} hover:${config?.text}`}>
                      <BookOpen className="w-4 h-4 mr-2" />
                      Explore
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="reflections" className="mt-4">
          {progress?.recent_completions?.length > 0 ? (
            <div className="space-y-3">
              {progress.recent_completions.map((completion, idx) => (
                <Card key={idx} className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-white">{completion.lesson_id}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(completion.completed_at).toLocaleDateString()}
                        </p>
                      </div>
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    </div>
                    {completion.reflection && (
                      <div className="mt-3 p-3 rounded-lg bg-zinc-800/50 text-sm text-zinc-300">
                        <Quote className="w-4 h-4 text-amber-400 mb-1" />
                        {completion.reflection}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Heart className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Your growth journey starts here</p>
              <p className="text-sm mt-1">Complete today's lesson to begin tracking your reflections</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Reflection Dialog */}
      <Dialog open={showReflectionDialog} onOpenChange={setShowReflectionDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-violet-400" />
              Take a Moment to Reflect
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-3">
              {lesson?.reflection_question}
            </p>
            <Textarea
              placeholder="What came up for you? (optional but valuable)"
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              className="bg-zinc-800 border-zinc-700 h-32"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReflectionDialog(false); handleComplete(); }} className="border-zinc-700">
              Skip for Now
            </Button>
            <Button
              onClick={handleComplete}
              disabled={completing}
              className="bg-gradient-to-r from-amber-500 to-orange-500"
            >
              {completing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Complete & Grow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lesson Detail Dialog */}
      <Dialog open={showLessonDetail} onOpenChange={setShowLessonDetail}>
        <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-700 max-h-[80vh] overflow-y-auto">
          {selectedLesson && (() => {
            const config = CATEGORY_CONFIG[selectedLesson.category];
            return (
              <>
                <DialogHeader>
                  <Badge className={`w-fit mb-2 ${config?.bg} ${config?.text} ${config?.border}`}>
                    {selectedLesson.category}
                  </Badge>
                  <DialogTitle className="text-xl">{selectedLesson.title}</DialogTitle>
                  <p className="text-muted-foreground">
                    {selectedLesson.author} • <span className="italic">{selectedLesson.book}</span>
                  </p>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div>
                    <h3 className={`text-sm font-semibold ${config?.text} mb-2`}>Key Insights</h3>
                    <ul className="space-y-2">
                      {selectedLesson.key_principles?.map((p, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                          <ChevronRight className={`w-4 h-4 ${config?.text} flex-shrink-0 mt-0.5`} />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className={`p-3 rounded-lg ${config?.bg} ${config?.border} border`}>
                    <h3 className={`text-sm font-semibold ${config?.text} mb-1`}>Application</h3>
                    <p className="text-sm text-zinc-300">{selectedLesson.application}</p>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
                    <h3 className="text-sm font-semibold text-violet-400 mb-1">Reflection</h3>
                    <p className="text-sm text-zinc-300 italic">"{selectedLesson.reflection_question}"</p>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <h3 className="text-sm font-semibold text-emerald-400 mb-1">Action</h3>
                    <p className="text-sm text-zinc-300">{selectedLesson.action_item}</p>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
